import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { publicAPI } from '../../lib/api';
import { formatSalary } from '../../lib/salary';
import { toDisplayHtml, toJsonLd, toPlainText } from '../../lib/html';
import { SafeHtml } from '../../components/SafeHtml';
import { MapPin, Briefcase, Clock, Wallet, Upload, CheckCircle2, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const SITE = 'https://www.chire.co';

const EMPLOYMENT_TYPE = { 'full-time': 'FULL_TIME', 'part-time': 'PART_TIME', contract: 'CONTRACTOR', internship: 'INTERN' };
const SALARY_UNIT = { year: 'YEAR', month: 'MONTH', hour: 'HOUR' };

function buildJsonLd(job, logoAbs, employer) {
  const ld = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: job.title,
    description: toDisplayHtml(job.description),
    datePosted: job.posted_at,
    employmentType: EMPLOYMENT_TYPE[job.job_type] || 'FULL_TIME',
    hiringOrganization: { '@type': 'Organization', name: employer || 'CHIRE Employer', ...(logoAbs ? { logo: logoAbs } : {}) },
    identifier: { '@type': 'PropertyValue', name: employer || 'CHIRE', value: job.id },
    directApply: true,
  };
  const isRemote = /remote|anywhere/i.test(job.location || '');
  if (isRemote) {
    ld.jobLocationType = 'TELECOMMUTE';
    ld.applicantLocationRequirements = { '@type': 'Country', name: job.location || 'Worldwide' };
  } else {
    ld.jobLocation = { '@type': 'Place', address: { '@type': 'PostalAddress', addressLocality: job.location || '' } };
  }
  if (job.salary_min || job.salary_max) {
    ld.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: job.salary_currency || 'USD',
      value: {
        '@type': 'QuantitativeValue',
        ...(job.salary_min ? { minValue: job.salary_min } : {}),
        ...(job.salary_max ? { maxValue: job.salary_max } : {}),
        unitText: SALARY_UNIT[job.salary_period] || 'YEAR',
      },
    };
  }
  return ld;
}

const inputStyle = { width: '100%', padding: '0.7rem 0.9rem', borderRadius: 12, border: '1px solid #ECE7DE', background: '#fff', fontSize: '0.875rem', color: '#002A38', outline: 'none', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#64748B', marginBottom: '0.375rem' };

export default function PublicJobPage() {
  const { jobId, companySlug } = useParams();
  const careersHref = companySlug ? `/${companySlug}/careers` : '/careers';
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    publicAPI.job(jobId)
      .then((res) => setJob(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [jobId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error('Name and email are required'); return; }
    if (!file) { toast.error('Please attach your resume'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('email', form.email.trim());
      fd.append('phone', form.phone.trim());
      const params = new URLSearchParams(window.location.search);
      fd.append('source', params.get('src') || params.get('utm_source') || params.get('source') || '');
      fd.append('referrer', document.referrer || '');
      fd.append('file', file);
      const res = await publicAPI.applyLead(jobId, fd);
      setDone(true);
      toast.success(res.data?.message || 'Application received!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B', background: '#F1EAE1' }}>Loading…</div>;
  if (notFound || !job) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: '#F1EAE1', fontFamily: "'Manrope', sans-serif" }}>
        <Helmet><title>Job not found — CHIRE</title><meta name="robots" content="noindex" /></Helmet>
        <Briefcase size={40} color="#94A3B8" />
        <div style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '1.25rem', fontWeight: 700, color: '#002A38' }}>This role is no longer open</div>
        <Link to={careersHref} style={{ color: '#0076A3', fontWeight: 600 }}>Browse open roles →</Link>
      </div>
    );
  }

  const sal = formatSalary(job);
  // Reached via the generic chire.co/careers board: no employer name or branding.
  const platform = !companySlug;
  const employer = platform ? 'CHIRE' : job.company;
  const brand = platform ? null : job.branding;
  const accent = (brand && brand.brand_color) || '#0076A3';
  const logoSrc = publicAPI.assetUrl(brand && brand.logo_url);
  const bannerSrc = publicAPI.assetUrl(brand && brand.banner_url);
  const meta = [{ Icon: Briefcase, v: job.job_type }, { Icon: MapPin, v: job.location }, { Icon: Clock, v: `${job.experience_years || 0}+ yrs` }].filter((m) => m.v);

  return (
    <div style={{ minHeight: '100vh', background: '#F1EAE1', fontFamily: "'Manrope', 'Inter', sans-serif" }}>
      <Helmet>
        <title>{platform ? `${job.title} — CHIRE Careers` : `${job.title} at ${job.company} — CHIRE Careers`}</title>
        <meta name="description" content={toPlainText(job.description).slice(0, 155)} />
        <link rel="canonical" href={companySlug ? `${SITE}/${companySlug}/careers/${job.id}` : `${SITE}/careers/${job.id}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${job.title} — ${employer}`} />
        <meta property="og:description" content={toPlainText(job.description).slice(0, 155)} />
        <script type="application/ld+json">{toJsonLd(buildJsonLd(job, logoSrc, employer))}</script>
      </Helmet>

      {/* Top bar */}
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #ECE7DE', position: 'sticky', top: 0, zIndex: 10 }}>
        {brand && (logoSrc || brand.company_name) ? (
          <div data-testid="brand-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {logoSrc && <img src={logoSrc} alt={job.company} style={{ height: 30, width: 'auto', maxWidth: 120, objectFit: 'contain' }} />}
            <span style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontWeight: 800, fontSize: '1.05rem', color: '#002A38', letterSpacing: '-0.01em' }}>{brand.company_name || job.company}</span>
          </div>
        ) : (
          <Link to={platform ? '/' : careersHref} data-testid="job-chire-logo"><img src="/chire-logo-full.png" alt="CHIRE" style={{ height: 46, objectFit: 'contain', display: 'block' }} /></Link>
        )}
        <Link to={careersHref} style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#475569', textDecoration: 'none' }}>All open roles</Link>
      </header>

      {/* Branded hero */}
      {brand && (
        <div data-testid="brand-hero" style={{ background: '#fff', borderBottom: '1px solid #ECE7DE' }}>
          <div style={{ height: 150, background: bannerSrc ? `center/cover no-repeat url(${bannerSrc})` : `linear-gradient(135deg, ${accent}, ${accent}bb)` }} />
          <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 1.5rem 1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginTop: -36 }}>
              <div style={{ width: 72, height: 72, borderRadius: 16, background: '#fff', border: '1px solid #ECE7DE', boxShadow: '0 8px 24px rgba(15,23,42,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                {logoSrc ? <img src={logoSrc} alt={job.company} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Building2 size={30} color={accent} />}
              </div>
              <div style={{ paddingBottom: 4 }}>
                <div style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '1.35rem', fontWeight: 700, color: '#002A38' }}>{brand.company_name || job.company}</div>
              </div>
            </div>
            {brand.blurb && <p style={{ fontSize: '0.9375rem', color: '#475569', marginTop: 12, lineHeight: 1.6, maxWidth: 640 }}>{brand.blurb}</p>}
          </div>
        </div>
      )}

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '2rem 1.5rem 4rem', display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
        {/* Job card */}
        <div data-testid="public-job-card" style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 18, padding: '2.25rem', boxShadow: '0 12px 40px rgba(15,23,42,0.05)' }}>
          {!platform && (
            <div data-testid="public-job-company" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem', color: '#64748B', fontWeight: 600, marginBottom: 10 }}>
              <Building2 size={15} /> {job.company}
            </div>
          )}
          <h1 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '2rem', fontWeight: 700, color: '#002A38', lineHeight: 1.15, margin: 0 }}>{job.title}</h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '1.1rem 0 1.5rem' }}>
            {meta.map((m, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 999, background: '#E5EFFF', color: '#0076A3', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' }}>
                <m.Icon size={13} /> {m.v}
              </span>
            ))}
            {sal && (
              <span data-testid="public-job-salary" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 999, background: '#CCFBF1', color: '#0F766E', fontSize: '0.75rem', fontWeight: 700 }}>
                <Wallet size={13} /> {sal}
              </span>
            )}
          </div>
          <SafeHtml className="rte-content" data-testid="public-job-description" html={job.description} style={{ fontSize: '0.9375rem', color: '#334155', lineHeight: 1.75 }} />
          {(job.required_skills || []).length > 0 && (
            <div style={{ marginTop: '1.75rem' }}>
              <div style={labelStyle}>Required Skills</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {job.required_skills.map((s) => (
                  <span key={s} style={{ padding: '4px 10px', borderRadius: 999, background: 'rgba(240, 80, 0,0.08)', border: '1px solid rgba(240, 80, 0,0.18)', fontSize: '0.75rem', color: '#0076A3', fontWeight: 600 }}>{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Apply */}
        <div id="apply" data-testid="public-apply-card" style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 18, padding: '2rem', boxShadow: '0 12px 40px rgba(15,23,42,0.05)' }}>
          {done ? (
            <div data-testid="apply-success" style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
              <CheckCircle2 size={44} color="#10B981" style={{ margin: '0 auto 1rem' }} />
              <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '1.25rem', fontWeight: 700, color: '#002A38' }}>Application received!</h2>
              <p style={{ fontSize: '0.9375rem', color: '#64748B', marginTop: 8 }}>Thanks, {form.name.split(' ')[0]}. The hiring team will review your profile and reach out if it's a match.</p>
              <Link to={careersHref} style={{ display: 'inline-block', marginTop: 16, color: '#0076A3', fontWeight: 600 }}>See other open roles →</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h2 style={{ fontFamily: "'Cabinet Grotesk', sans-serif", fontSize: '1.25rem', fontWeight: 700, color: '#002A38', marginBottom: 4 }}>Apply for this role</h2>
              <p style={{ fontSize: '0.8125rem', color: '#64748B', marginBottom: '1.25rem' }}>Takes under a minute. Just your details and a resume.</p>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>Full Name *</label>
                  <input data-testid="apply-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" style={inputStyle} required />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Email *</label>
                    <input data-testid="apply-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@email.com" style={inputStyle} required />
                  </div>
                  <div>
                    <label style={labelStyle}>Phone</label>
                    <input data-testid="apply-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Optional" style={inputStyle} />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Resume *</label>
                  <label data-testid="apply-file-label" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.9rem 1rem', borderRadius: 12, border: `1px dashed ${file ? '#0D9488' : '#CBD5E1'}`, background: file ? '#F0FDFA' : '#FAF7F2', cursor: 'pointer' }}>
                    <Upload size={18} color={file ? '#0D9488' : '#94A3B8'} />
                    <span style={{ fontSize: '0.8125rem', color: file ? '#0F766E' : '#64748B', fontWeight: file ? 600 : 400 }}>{file ? file.name : 'Upload PDF, DOCX or TXT'}</span>
                    <input data-testid="apply-file" type="file" accept=".pdf,.docx,.txt" onChange={(e) => setFile(e.target.files[0])} style={{ display: 'none' }} />
                  </label>
                </div>
                <button data-testid="apply-submit" type="submit" disabled={submitting} style={{ width: '100%', padding: '0.85rem', borderRadius: 999, border: 'none', background: submitting ? '#94A3B8' : accent, color: '#fff', fontWeight: 700, fontSize: '0.9375rem', cursor: submitting ? 'not-allowed' : 'pointer' }}>
                  {submitting ? 'Submitting…' : 'Submit Application'}
                </button>
                <p style={{ fontSize: '0.6875rem', color: '#94A3B8', textAlign: 'center', lineHeight: 1.5 }}>
                  By applying you agree to CHIRE processing your details for this role. See our <Link to="/legal/privacy" style={{ color: '#64748B' }}>Privacy Policy</Link>.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
